import { Block, type Change, Inline } from '@gitbook/slate';

import { BLOCK_MATH, INLINE_MATH } from '../types';

const defaultFormula = 'f(x) = x * e^{2 pi i \\xi x}';

/**
 * Create a TeX math block
 */
export function createMathBlock(formula: string = defaultFormula) {
    return Block.createVoid({
        type: BLOCK_MATH,
        data: {
            formula,
        },
    });
}

/**
 * Create a TeX math line
 */
export function createMathInline(formula: string = defaultFormula) {
    return Inline.createVoid({
        type: INLINE_MATH,
        data: {
            formula,
        },
    });
}

/**
 * Set the math formula on a block/inline.
 *
 * It's designed to be extremely safe in order to track down a bug in the app where we set the formula to undefined.
 */
export function setFormula(change: Change, node: Block | Inline, formula: string): Change {
    if (!formula || typeof formula === 'undefined') {
        throw new Error(`Tried to set an undefined formula for math node ${node.key}`);
    }

    const result = change.setDataByKey(
        node.key,
        ['formula'],
        node.object === 'inline' ? formula.replace(/\n/g, ' ') : formula,
        { normalize: true }
    );

    const nodeAfter = change.value.document.assertNode(node.key);

    if (
        (Block.isBlock(nodeAfter) || Inline.isInline(nodeAfter)) &&
        typeof nodeAfter.data.get('formula') === 'undefined'
    ) {
        throw new Error(`Math formula is undefined after change ${node.key}`);
    }

    return result;
}
